import React, { useEffect, useState } from 'react';
import { AppData } from './PageContext';
import { getPermission } from '@/modules/accounts/viewmodels/getPermission';
import { authenticate, getCustomTokenFromSSOServer } from '../auth';
import { AllUsersUseCase } from '@/domain/use-cases/users';
import { UsersRepoIml } from '@/modules/users/adapters/repositories';
import { getUserByFirebaseId } from '@/modules/users/viewmodels/findFirebaseId';
import { UsersEntity } from '@/domain/entities/users';
import { SocketManager } from '@mx/socket/dist/socket';
import { useSSOAuthentication } from '../hooks';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { config } from '@/config';
import { getIdToken, onAuthStateChanged } from 'firebase/auth';
import { auth } from '@/common/firebase';
import { useNavigate } from 'react-router-dom';

interface Data {
  t: TFunction;
  permission: string[];
  userData: UsersEntity;
  changeLanguage(lng: string): Promise<TFunction>;
  socketManager: SocketManager | undefined;
  currentLanguage: string;
}

const validateCriticalConfig = () => {
  const criticalKeys = ['identity.url', 'base.apiUrl', 'firebase.projectId'];
  const missingKeys: string[] = [];

  if (!config.identity?.url) missingKeys.push('identity.url');
  if (!config.base?.apiUrl) missingKeys.push('base.apiUrl');
  if (!config.firebase?.projectId) missingKeys.push('firebase.projectId');

  if (missingKeys.length > 0) {
    console.error(
      `Critical configuration missing: ${missingKeys.join(
        ', ',
      )}. Check environment injection or override.config.ts.`,
    );
  }
};

export const AppProvider = (props: any) => {
  const navigate = useNavigate();
  const { actionGetUserByFirebaseId, userData } = getUserByFirebaseId();
  const [socketManager, setSocketManager] = useState<SocketManager | undefined>(undefined);

  const findUser = async (uid: string) => {
    if (uid) {
      actionGetUserByFirebaseId(uid);
    }
  };

  const navigateToLogin = () => {
    navigate('/login');
  };

  useEffect(() => {
    validateCriticalConfig();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        const uid = user.uid;
        findUser(uid);

        const socketEndpoint = config.socketService.main;
        const socket = new SocketManager(user.getIdToken, socketEndpoint);

        setSocketManager(socket);
      } else {
        navigateToLogin();
      }
    });
    return unsubscribe;
  }, []);

  const data: Data = {
    permission: [],
    userData: userData,
    socketManager,
    t: useTranslation().t,
    currentLanguage: i18n.language || 'vi',
    changeLanguage: i18n.changeLanguage,
  };

  return (
    <AppData {...data} userData={userData}>
      {props.children}
    </AppData>
  );
};
